import { useCallback, useSyncExternalStore } from 'react';
import { safeStorage } from '@/lib/safe-storage';

/**
 * Light / dark / system theme persistence (spec §14).
 * Mirrors the admin + mobile implementations.
 *
 * Module-level store (same pattern as `useStoredTenantId`): the mode is
 * shared by every hook instance, so the App-root instance and the
 * Profile ThemeSwitcher can never disagree. With per-instance state, a
 * root instance started in 'system' kept its matchMedia listener alive
 * after the user picked an explicit theme in Profile — the next OS
 * scheme flip would silently revert the user's choice.
 */
export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'mycity.theme';
const listeners = new Set<() => void>();

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = safeStorage.get(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

let currentMode: ThemeMode = readStoredMode();

function resolveTheme(mode: ThemeMode): 'light' | 'dark' {
  if (mode === 'light' || mode === 'dark') return mode;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(mode: ThemeMode): void {
  document.documentElement.classList.toggle('dark', resolveTheme(mode) === 'dark');
}

// One matchMedia subscription for the whole app, only meaningful while
// mode === 'system' (the handler checks, rather than churning listeners).
// An OS scheme flip changes the RESOLVED theme even though the mode is
// unchanged, so subscribers (e.g. map remount keys) must be notified.
if (typeof window !== 'undefined') {
  applyTheme(currentMode);
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (currentMode === 'system') {
      applyTheme('system');
      listeners.forEach((l) => l());
    }
  });
}

function setThemeMode(next: ThemeMode): void {
  safeStorage.set(STORAGE_KEY, next);
  currentMode = next;
  applyTheme(next);
  listeners.forEach((l) => l());
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useThemeMode(): { mode: ThemeMode; setMode: (next: ThemeMode) => void } {
  const mode = useSyncExternalStore(
    subscribe,
    () => currentMode,
    () => 'system' as const,
  );
  const setMode = useCallback((next: ThemeMode) => setThemeMode(next), []);
  return { mode, setMode };
}

/**
 * The theme actually applied ('light' | 'dark'), resolving 'system'.
 *
 * Map surfaces key + theme-prop the vendored <Map> with this value so a
 * theme flip REMOUNTS the map and swaps tile styles cleanly (layers
 * re-add on mount). The vendored auto-detection can't be trusted here:
 * it falls back to the OS scheme whenever the `dark` class is absent, so
 * an explicit light choice on a dark-mode OS rendered dark tiles forever
 * (Peyman's "map is always dark" report).
 */
export function useResolvedTheme(): 'light' | 'dark' {
  return useSyncExternalStore(
    subscribe,
    () => resolveTheme(currentMode),
    () => 'light' as const,
  );
}
